import { useState, useCallback, useEffect } from 'react'
import { UserConfig, KeyPress, AnalysisResult, PlotSelection, ReplayAnalysisResult } from './types'
import { useKeyCapture } from './hooks/useKeyCapture'
import InteractivePlot from './components/InteractivePlot'
import { analyzeLiveKeypresses, analyzeReplayFile } from './utils/api'
import { calculateStatistics, filterKeypressesBySelection, formatBpm, formatDuration, analyzeKeypressesLocally } from './utils/calculations'
import './App.css'

// Default configuration
const defaultConfig: UserConfig = {
    key1: 'z',
    key2: 'x',
    detection: {
        bpmThreshold: 450,
        durationThreshold: 5
    }
}

type AppMode = 'live' | 'replay'

// Convert BPM to CPS (1/4 streaming = 4 notes per beat)
const bpmToCps = (bpm: number) => (bpm * 4) / 60;
const cpsToBpm = (cps: number) => (cps * 60) / 4;

// Format speed value based on display mode
const formatSpeed = (bpm: number, showCps: boolean) => {
    if (showCps) {
        return `${bpmToCps(bpm).toFixed(1)}`;
    }
    return formatBpm(bpm);
};

function App() {
    const [mode, setMode] = useState<AppMode>('live')
    const [config, setConfig] = useState<UserConfig>(defaultConfig)
    const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null)
    const [selection, setSelection] = useState<PlotSelection | null>(null)
    const [isAnalyzing, setIsAnalyzing] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [showCps, setShowCps] = useState(false)
    const [plotKey, setPlotKey] = useState(0) // Force plot remount on mode change

    // Replay mode state
    const [replayFile, setReplayFile] = useState<File | null>(null)
    const [replayResult, setReplayResult] = useState<ReplayAnalysisResult | null>(null)
    const [replayKeypresses, setReplayKeypresses] = useState<KeyPress[]>([])

    // Key capture hook
    const { state: captureState, startCapture, stopCapture, reset } = useKeyCapture({
        key1: config.key1,
        key2: config.key2
    })

    // Get current keypresses based on mode
    const currentKeypresses = mode === 'live' ? captureState.keypresses : replayKeypresses

    // Calculate selection statistics
    const selectionStats = selection && currentKeypresses.length > 0
        ? calculateStatistics(filterKeypressesBySelection(currentKeypresses, selection))
        : null;

    // Force plot remount when changing modes
    useEffect(() => {
        setPlotKey(prev => prev + 1)
    }, [mode])

    // Handle starting the test
    const handleStartTest = useCallback(() => {
        setError(null)
        setAnalysisResult(null)
        setSelection(null)
        startCapture()
    }, [startCapture])

    // Handle stopping the test and running analysis
    const handleStopTest = useCallback(async () => {
        stopCapture()

        setTimeout(async () => {
            const keypresses = captureState.keypresses
            if (keypresses.length < 2) {
                setError('Not enough keypresses to analyze. Please press more keys.')
                return
            }

            setIsAnalyzing(true)
            setError(null)

            try {
                const result = await analyzeLiveKeypresses(keypresses, config.detection)
                setAnalysisResult(result)
            } catch (err) {
                console.error('Backend unavailable, using client-side analysis:', err)
                // Use proper sliding window detection locally
                const result = analyzeKeypressesLocally(
                    keypresses,
                    config.detection.bpmThreshold,
                    config.detection.durationThreshold
                )
                setAnalysisResult(result)
            } finally {
                setIsAnalyzing(false)
            }
        }, 100)
    }, [stopCapture, captureState.keypresses, config.detection])

    // Handle reset
    const handleReset = useCallback(() => {
        reset()
        setAnalysisResult(null)
        setSelection(null)
        setError(null)
    }, [reset])

    // Handle replay file upload
    const handleReplayFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        setReplayFile(file)
        setError(null)
        setIsAnalyzing(true)
        setSelection(null)

        try {
            const result = await analyzeReplayFile(
                file,
                config.detection.bpmThreshold,
                config.detection.durationThreshold
            )
            setReplayResult(result)
            setReplayKeypresses(result.keypresses)
            setAnalysisResult(result.analysis)
            setPlotKey(prev => prev + 1) // Force plot refresh
        } catch (err) {
            console.error('Replay analysis failed:', err)
            setError('Failed to analyze replay. Make sure the backend server is running.')
        } finally {
            setIsAnalyzing(false)
        }
    }, [config.detection])

    // Re-analyze replay when thresholds change (use local analysis if we have keypresses)
    useEffect(() => {
        if (mode === 'replay' && replayKeypresses.length > 0) {
            // Use local analysis since we already have keypresses loaded
            const result = analyzeKeypressesLocally(
                replayKeypresses,
                config.detection.bpmThreshold,
                config.detection.durationThreshold
            )
            setAnalysisResult(result)
        }
    }, [mode, replayKeypresses, config.detection.bpmThreshold, config.detection.durationThreshold])

    const currentHighlightZones = analysisResult?.highlightZones ?? []
    const speedLabel = showCps ? 'CPS' : 'BPM'

    // Render selection stats overlay
    const renderSelectionStats = () => {
        if (!selectionStats || selectionStats.totalInputs === 0 || !selection) return null;

        return (
            <div className="selection-overlay">
                <div className="selection-stats">
                    <h3>Selection ({selection.startTime.toFixed(2)}s - {selection.endTime.toFixed(2)}s)</h3>
                    <div className="statistics">
                        <div className="stat">
                            <span className="stat-label">Inputs</span>
                            <span className="stat-value">{selectionStats.totalInputs}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Duration</span>
                            <span className="stat-value">{formatDuration(selectionStats.duration)}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Avg {speedLabel}</span>
                            <span className="stat-value">{formatSpeed(selectionStats.avgBpm, showCps)}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Avg Hold</span>
                            <span className="stat-value">{selectionStats.avgHoldTime.toFixed(0)}ms</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Min Hold</span>
                            <span className="stat-value">{selectionStats.minHoldTime.toFixed(0)}ms</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Max Hold</span>
                            <span className="stat-value">{selectionStats.maxHoldTime.toFixed(0)}ms</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="app">
            <header className="app-header">
                <h1>osu! Rake Tap Detection</h1>
                <p className="subtitle">Detect rake tapping patterns</p>
                <div className="display-toggle">
                    <button
                        className={`toggle-btn ${!showCps ? 'active' : ''}`}
                        onClick={() => setShowCps(false)}
                    >
                        BPM
                    </button>
                    <button
                        className={`toggle-btn ${showCps ? 'active' : ''}`}
                        onClick={() => setShowCps(true)}
                    >
                        CPS
                    </button>
                </div>
                <nav className="mode-selector">
                    <button
                        className={`mode-btn ${mode === 'live' ? 'active' : ''}`}
                        onClick={() => { setMode('live'); setSelection(null); }}
                    >
                        Live Test
                    </button>
                    <button
                        className={`mode-btn ${mode === 'replay' ? 'active' : ''}`}
                        onClick={() => { setMode('replay'); setSelection(null); }}
                    >
                        Replay
                    </button>
                </nav>
            </header>

            <main className="main-content">
                {mode === 'live' ? (
                    <div className="live-mode">
                        <div className="left-panel">
                            <div className="config-panel">
                                <h2>Keys</h2>
                                <div className="config-row">
                                    <label>
                                        Key 1
                                        <input
                                            type="text"
                                            maxLength={1}
                                            value={config.key1}
                                            onChange={(e) => setConfig({ ...config, key1: e.target.value.toLowerCase() || 'z' })}
                                            disabled={captureState.isRunning}
                                        />
                                    </label>
                                    <label>
                                        Key 2
                                        <input
                                            type="text"
                                            maxLength={1}
                                            value={config.key2}
                                            onChange={(e) => setConfig({ ...config, key2: e.target.value.toLowerCase() || 'x' })}
                                            disabled={captureState.isRunning}
                                        />
                                    </label>
                                </div>
                                <h2 style={{ marginTop: '0.75rem' }}>Detection</h2>
                                <div className="config-row">
                                    <label>
                                        {speedLabel} Threshold
                                        <input
                                            type="number"
                                            value={showCps ? bpmToCps(config.detection.bpmThreshold).toFixed(1) : config.detection.bpmThreshold}
                                            onChange={(e) => {
                                                const val = Number(e.target.value);
                                                setConfig({
                                                    ...config,
                                                    detection: {
                                                        ...config.detection,
                                                        bpmThreshold: showCps ? cpsToBpm(val) : val || 450
                                                    }
                                                });
                                            }}
                                        />
                                    </label>
                                    <label>
                                        Duration (s)
                                        <input
                                            type="number"
                                            step="0.5"
                                            value={config.detection.durationThreshold}
                                            onChange={(e) =>
                                                setConfig({
                                                    ...config,
                                                    detection: { ...config.detection, durationThreshold: Number(e.target.value) || 5 }
                                                })
                                            }
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="test-area">
                                <h2>Test Controls</h2>
                                <p className="instructions">
                                    Press <kbd>{config.key1.toUpperCase()}</kbd> + <kbd>{config.key2.toUpperCase()}</kbd>
                                    {captureState.isRunning && <span className="recording"> • REC</span>}
                                </p>
                                <div className="test-controls">
                                    <button
                                        className="start-btn"
                                        onClick={handleStartTest}
                                        disabled={captureState.isRunning}
                                    >
                                        Start
                                    </button>
                                    <button
                                        className="stop-btn"
                                        onClick={handleStopTest}
                                        disabled={!captureState.isRunning}
                                    >
                                        Stop
                                    </button>
                                    <button
                                        className="reset-btn"
                                        onClick={handleReset}
                                        disabled={captureState.isRunning || captureState.keypresses.length === 0}
                                    >
                                        Reset
                                    </button>
                                </div>
                                <div className="live-stats">
                                    <span>Inputs: {captureState.keypresses.length}</span>
                                    {captureState.keypresses.length > 0 && (
                                        <>
                                            <span>Duration: {formatDuration((captureState.keypresses[captureState.keypresses.length - 1]?.endTime ?? 0) / 1000)}</span>
                                            <span>{speedLabel}: {formatSpeed(calculateStatistics(captureState.keypresses).avgBpm, showCps)}</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {error && <div className="error-message">{error}</div>}
                            {isAnalyzing && <div className="analyzing">Analyzing...</div>}

                            {analysisResult && !captureState.isRunning && (
                                <>
                                    <div className="results-panel">
                                        <h2>Total Results</h2>
                                        <div className={`rake-status ${analysisResult.isRaking ? 'raking' : 'normal'}`}>
                                            {analysisResult.isRaking ? 'Rake Detected' : 'Normal'}
                                        </div>
                                        <div className="statistics">
                                            <div className="stat">
                                                <span className="stat-label">Inputs</span>
                                                <span className="stat-value">{analysisResult.statistics.totalInputs}</span>
                                            </div>
                                            <div className="stat">
                                                <span className="stat-label">Duration</span>
                                                <span className="stat-value">{formatDuration(analysisResult.statistics.duration)}</span>
                                            </div>
                                            <div className="stat">
                                                <span className="stat-label">Avg {speedLabel}</span>
                                                <span className="stat-value">{formatSpeed(analysisResult.statistics.avgBpm, showCps)}</span>
                                            </div>
                                            <div className="stat">
                                                <span className="stat-label">Avg Hold</span>
                                                <span className="stat-value">{analysisResult.statistics.avgHoldTime.toFixed(0)}ms</span>
                                            </div>
                                            <div className="stat">
                                                <span className="stat-label">Min Hold</span>
                                                <span className="stat-value">{analysisResult.statistics.minHoldTime.toFixed(0)}ms</span>
                                            </div>
                                            <div className="stat">
                                                <span className="stat-label">Max Hold</span>
                                                <span className="stat-value">{analysisResult.statistics.maxHoldTime.toFixed(0)}ms</span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="right-panel">
                            <div className="plot-area">
                                <h2>Hold Times {captureState.keypresses.length > 0 && '(drag to select)'}</h2>
                                <div className="plot-container">
                                    <InteractivePlot
                                        key={`live-${plotKey}`}
                                        keypresses={captureState.keypresses}
                                        highlightZones={currentHighlightZones}
                                        onSelectionChange={setSelection}
                                        isLive={captureState.isRunning}
                                    />
                                    {renderSelectionStats()}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="replay-mode">
                        <div className="left-panel">
                            <div className="upload-area">
                                <h2>Upload .osr</h2>
                                <div className="upload-box">
                                    <p>{replayFile ? replayFile.name : 'Click or drop file'}</p>
                                    <input
                                        type="file"
                                        accept=".osr"
                                        onChange={handleReplayFileChange}
                                    />
                                </div>
                            </div>

                            <div className="config-panel">
                                <h2>Detection</h2>
                                <div className="config-row">
                                    <label>
                                        {speedLabel} Threshold
                                        <input
                                            type="number"
                                            value={showCps ? bpmToCps(config.detection.bpmThreshold).toFixed(1) : config.detection.bpmThreshold}
                                            onChange={(e) => {
                                                const val = Number(e.target.value);
                                                setConfig({
                                                    ...config,
                                                    detection: {
                                                        ...config.detection,
                                                        bpmThreshold: showCps ? cpsToBpm(val) : val || 450
                                                    }
                                                });
                                            }}
                                        />
                                    </label>
                                    <label>
                                        Duration (s)
                                        <input
                                            type="number"
                                            step="0.5"
                                            value={config.detection.durationThreshold}
                                            onChange={(e) =>
                                                setConfig({
                                                    ...config,
                                                    detection: { ...config.detection, durationThreshold: Number(e.target.value) || 5 }
                                                })
                                            }
                                        />
                                    </label>
                                </div>
                            </div>

                            {replayResult && (
                                <div className="replay-info">
                                    <h2>Replay</h2>
                                    <div className="info-grid">
                                        <span className="label">Player:</span>
                                        <span className="value">{replayResult.replayInfo.username}</span>
                                        <span className="label">Mods:</span>
                                        <span className="value">{replayResult.replayInfo.mods.join(', ') || 'None'}</span>
                                    </div>
                                </div>
                            )}

                            {error && <div className="error-message">{error}</div>}
                            {isAnalyzing && <div className="analyzing">Analyzing...</div>}

                            {analysisResult && (
                                <div className="results-panel">
                                    <h2>Total Results</h2>
                                    <div className={`rake-status ${analysisResult.isRaking ? 'raking' : 'normal'}`}>
                                        {analysisResult.isRaking ? 'Rake Detected' : 'Normal'}
                                    </div>
                                    <div className="statistics">
                                        <div className="stat">
                                            <span className="stat-label">Inputs</span>
                                            <span className="stat-value">{analysisResult.statistics.totalInputs}</span>
                                        </div>
                                        <div className="stat">
                                            <span className="stat-label">Duration</span>
                                            <span className="stat-value">{formatDuration(analysisResult.statistics.duration)}</span>
                                        </div>
                                        <div className="stat">
                                            <span className="stat-label">Avg {speedLabel}</span>
                                            <span className="stat-value">{formatSpeed(analysisResult.statistics.avgBpm, showCps)}</span>
                                        </div>
                                        <div className="stat">
                                            <span className="stat-label">Avg Hold</span>
                                            <span className="stat-value">{analysisResult.statistics.avgHoldTime.toFixed(0)}ms</span>
                                        </div>
                                        <div className="stat">
                                            <span className="stat-label">Min Hold</span>
                                            <span className="stat-value">{analysisResult.statistics.minHoldTime.toFixed(0)}ms</span>
                                        </div>
                                        <div className="stat">
                                            <span className="stat-label">Max Hold</span>
                                            <span className="stat-value">{analysisResult.statistics.maxHoldTime.toFixed(0)}ms</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="right-panel">
                            <div className="plot-area">
                                <h2>Hold Times {replayKeypresses.length > 0 && '(drag to select)'}</h2>
                                <div className="plot-container">
                                    <InteractivePlot
                                        key={`replay-${plotKey}`}
                                        keypresses={replayKeypresses}
                                        highlightZones={currentHighlightZones}
                                        onSelectionChange={setSelection}
                                    />
                                    {renderSelectionStats()}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    )
}

export default App